import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { isBlankFile } from "../../services/lawyerApi";

// Round avatar; falls back to a grey silhouette when there is no real picture.
export const Avatar = ({ src, alt = "", size = 64, className = "" }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const style = { width: size, height: size };

  if (isBlankFile(src) || broken) {
    return (
      <span className={`lwp-avatar-fallback ${className}`} style={style} aria-label={alt}>
        <i className="bi bi-person-fill" style={{ fontSize: size * 0.55 }} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`lwp-avatar-img ${className}`}
      style={style}
      onError={() => setBroken(true)}
    />
  );
};

export const PageHeader = ({ title, backTo = "/lawyer/dashboard", actions, children }) => (
  <header className="lwp-page-head">
    <Link to={backTo} className="lwp-back" aria-label="Back">
      <i className="bi bi-chevron-left" />
    </Link>
    <div className="lwp-page-head-title">
      <h1>{title}</h1>
      {children}
    </div>
    {actions && <div className="lwp-page-head-actions">{actions}</div>}
  </header>
);

// Centered dialog on desktop, bottom sheet on phones.
export const Modal = ({ open, onClose, title, children, footer, className = "" }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="lwp lwp-modal-root" role="presentation" onMouseDown={onClose}>
      <div
        className={`lwp-modal ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="lwp-modal-grip" />
        {title && (
          <div className="lwp-modal-head">
            <h2>{title}</h2>
            <button type="button" className="lwp-icon-btn lwp-modal-close" onClick={onClose} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>
          </div>
        )}
        <div className="lwp-modal-body">{children}</div>
        {footer && <div className="lwp-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export const Switch = ({ checked, onChange, disabled, label }) => (
  <label className="lwp-switch" onClick={(e) => e.stopPropagation()}>
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} aria-label={label} />
    <span className="lwp-switch-track" />
  </label>
);

export const Spinner = () => (
  <div className="lwp-spinner-wrap">
    <span className="spinner-border spinner-border-sm" />
  </div>
);

export const EmptyState = ({ icon = "bi-inbox", text }) => (
  <div className="lwp-placeholder">
    <i className={`bi ${icon}`} />
    <p>{text}</p>
  </div>
);

// Pill-style tab switcher. tabs: [{ key, label, count? }]
export const SegmentTabs = ({ tabs, value, onChange, label }) => (
  <div className="lwp-segment" role="tablist" aria-label={label}>
    {tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        role="tab"
        aria-selected={value === t.key}
        className={`lwp-segment-tab${value === t.key ? " active" : ""}`}
        onClick={() => onChange(t.key)}
      >
        {t.label}
        {t.count > 0 && <span className="lwp-segment-count">{t.count > 99 ? "99+" : t.count}</span>}
      </button>
    ))}
  </div>
);

// Paged list loader shared by the list screens: returns items plus paging helpers.
export const usePagedList = (fetchPage, extract, deps = []) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [meta, setMeta] = useState({});
  const [moreError, setMoreError] = useState("");
  // Bumped whenever deps change so late responses for an old query are dropped.
  const generation = useRef(0);

  const load = async (nextPage) => {
    const gen = generation.current;
    const res = await fetchPage(nextPage);
    if (gen !== generation.current) return;
    if (!res?.data?.status) throw new Error(res?.data?.message || "Unable to load.");
    const data = res.data.data || {};
    const list = extract(data) || [];
    setItems((prev) => (nextPage === 1 ? list : [...prev, ...list]));
    setPage(data.pagination?.current_page || nextPage);
    setLastPage(data.pagination?.last_page || 1);
    setMeta(data);
  };

  useEffect(() => {
    let active = true;
    generation.current += 1;
    setLoading(true);
    setItems([]);
    setPage(1);
    setLastPage(1);
    setMoreError("");
    load(1)
      .catch((err) => active && setMeta({ error: err?.response?.data?.message || err.message }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = async () => {
    if (loadingMore || page >= lastPage) return;
    setLoadingMore(true);
    setMoreError("");
    try {
      await load(page + 1);
    } catch (err) {
      setMoreError(err?.response?.data?.message || err.message || "Unable to load more.");
    } finally {
      setLoadingMore(false);
    }
  };

  return { items, setItems, loading, loadingMore, hasMore: page < lastPage, loadMore, moreError, meta };
};

// auto → also loads the next page when the button scrolls near the viewport
// (stops auto-loading after an error so a failing page isn't retried in a loop).
export const LoadMore = ({ list, auto }) => {
  const ref = useRef(null);
  const loadMore = useRef(list.loadMore);
  loadMore.current = list.loadMore;
  const watch = auto && list.hasMore && !list.loadingMore && !list.moreError;

  useEffect(() => {
    if (!watch || !ref.current) return;
    const observer = new IntersectionObserver((entries) => entries[0].isIntersecting && loadMore.current(), {
      rootMargin: "300px",
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [watch, list.items.length]);

  return list.hasMore ? (
    <button ref={ref} type="button" className="lwp-load-more" onClick={list.loadMore} disabled={list.loadingMore}>
      {list.loadingMore ? (
        <span className="spinner-border spinner-border-sm" />
      ) : list.moreError ? (
        "Couldn't load more. Retry"
      ) : (
        "Load more"
      )}
    </button>
  ) : null;
};

export const isPdf = (fileOrUrl) =>
  fileOrUrl instanceof File ? fileOrUrl.type === "application/pdf" : /\.pdf($|\?)/i.test(fileOrUrl || "");

// A picked file with its preview URL; existing uploads are plain URLs.
export const withPreview = (file) => ({ file, src: URL.createObjectURL(file) });
export const revoke = (item) => item?.file && URL.revokeObjectURL(item.src);

// Dropdown-style field that opens a searchable sheet. multi → value is an array.
// hideLabel → the placeholder carries the label, as on the signup form.
export const PickerField = ({ label, options, value, onChange, multi, placeholder = "Select", disabled, hideLabel, invalid }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = multi ? value : value ? [value] : [];
  const summary = multi
    ? selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label || "1 Selected"
      : selected.length
      ? `${selected.length} Selected`
      : ""
    : options.find((o) => o.value === value)?.label || value || "";

  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  const pick = (v) => {
    if (!multi) {
      onChange(v);
      setOpen(false);
      return;
    }
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="lwp-field">
      {!hideLabel && <span>{label}</span>}
      <button
        type="button"
        className={`lwp-picker${invalid ? " is-invalid" : ""}`}
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label={hideLabel ? label : undefined}
      >
        <span className={summary ? "" : "lwp-muted"}>{summary || placeholder}</span>
        <i className="bi bi-chevron-down" />
      </button>
      <Modal
        open={open}
        onClose={close}
        title={label}
        className="lwp-picker-modal"
        footer={
          multi && (
            <button type="button" className="lwp-btn" onClick={close}>
              Done{selected.length ? ` (${selected.length})` : ""}
            </button>
          )
        }
      >
        {options.length > 8 && (
          <input
            className="lwp-input lwp-input-box lwp-picker-search"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
        {shown.length === 0 ? (
          <p className="lwp-muted lwp-picker-empty">No options found</p>
        ) : (
          <ul className="lwp-picker-list">
            {shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <li key={o.value}>
                  <button type="button" className={on ? "active" : ""} onClick={() => pick(o.value)} aria-pressed={on}>
                    <span>{o.label}</span>
                    <i className={`bi ${on ? (multi ? "bi-check-square-fill" : "bi-check-circle-fill") : multi ? "bi-square" : "bi-circle"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>
    </div>
  );
};

export const FilePreview = ({ item, label }) => {
  const src = item?.src;
  if (!src) return null;
  if (isPdf(item.file || src)) return <i className="bi bi-file-earmark-pdf-fill" aria-label={label} />;
  return <img src={src} alt={label} />;
};
